import { useState, useEffect, useLayoutEffect, useContext, useRef } from "react";
import { StateContext } from "../../../../context/globalContext/context";
import { iGlobalContext } from "../../../../context/globalContext/interface";
import useCustomErrorHandler from "../../../../hooks/useCustomErrorHandler";
import { getPromptTemplateAPI } from "../../../../api/config/getPrompt";
import { getConfigWithFilter } from "../../../../api/config/getConfig";
import { getAppToastMessage } from "../../../../api/appMessages/appMessages";
import { updateToastMessages } from "../../../../constant/messages/toastMessages";
import { isValidJWT } from "../../../../utils/authentication/isValidJWT";
import { consoleLog } from "../../../../utils/consoleLog/consoleLog";
import { cleanupStaleBuild } from "../../../../utils/staleBuildCleanup";
import { resolveBoolFlag } from "../../../../utils/appConfig/resolveBoolFlag";
import { setJoiValidationEnabled } from "../../../../utils/booking/travelerForm/joiSchemas";
import { IS_ENV_LOADED, APP_VERSION, ACCESS_JWT } from "../../../../constant/environment";
import { getUserLocationAPI } from "../../../../api/config/getUserLocation";
import { fetchAndCacheGeoCountry, getCachedGeoCountryIso } from "../../../../utils/phoneUtils/geoCountry";
import { getDeviceId } from "../../../../utils/rateLimit/rateLimit";
import { getItemFromLocalStorage, setItemInLocalStorage } from "../../../../hooks/localStorage";
import { APP_CONFIG as COMMON_APP_CONFIG, LOCAL_STORAGE_KEY } from "../../../../constant/common"; // APP_CONFIG name clash?
import { getConfigMessage } from "../../../../utils";
import { useGlobalAction } from "../../../../hooks/useGlobalAction";
import { clearAllLocalSessionStorageThenRefresh } from "../../../../utils/clearAllLocalSessionStorageThenRefresh";
import { fetchCountries } from "../../../../api/booking/booking";
import { getUserDetailsApi } from "../../../../api/user/getUserDetails";
import { useCustomSideBarLogic } from "../customSideBar/hooks/useCustomSideBarLogic";

// --- Connectivity detection tunables ---
// `navigator.onLine` is unreliable (false-negatives on Windows / VPN / virtual
// network adapters even when the internet works), so we never trust it alone.
// Instead we do a REAL reachability probe (HEAD a tiny same-origin asset) and only
// show the "no internet" page after connectivity has failed continuously for
// OFFLINE_THRESHOLD_MS. This also naturally covers "unusably slow" internet: if the
// probe can't complete within that window it counts as a failure.
// EVENT-DRIVEN: while online we do NOT poll in the background (no network spam) — we
// probe only on mount, on the browser online/offline events, on tab re-focus, and
// (once a failure streak starts) every FAILING_RECHECK_MS until it recovers.
const CONNECTIVITY_PROBE_URL = "/favicon.ico"; // exists in public/, same-origin (no CORS)
const OFFLINE_THRESHOLD_MS = 11000; // probe timeout AND how long a failure streak must last before the page shows
const FAILING_RECHECK_MS = 2500; // re-probe cadence WHILE failing (reach the threshold + detect recovery); no polling while online

/**
 * Returns true if the network is reachable. A resolved fetch (ANY http status,
 * even 404) means the round-trip succeeded → online. A rejection or a
 * OFFLINE_THRESHOLD_MS timeout means offline / unusably-slow. Cache-buster +
 * `no-store` guarantee a real network hit (no service worker in this app).
 */
async function probeConnectivity(): Promise<boolean> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), OFFLINE_THRESHOLD_MS);
  try {
    await fetch(`${CONNECTIVITY_PROBE_URL}?_=${Date.now()}`, {
      method: "HEAD",
      cache: "no-store",
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

export const useAppInitialization = () => {
  const {
    accessJWT,
    setAccessJWT,
    setAppConfig,
    setPromptTemplate,
    userInfo,
    setUserLocation,
    isValidAccessToken,
    setCountriesData,
    countriesData,
    appConfig,
  }: iGlobalContext = useContext(StateContext);

  const { handleCustomError } = useCustomErrorHandler();
  const { handleLogoutClick } = useCustomSideBarLogic();
  const [loading, setLoading] = useState(true);
  const [networkError, setNetworkError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [envLoading, setEnvLoading] = useState(IS_ENV_LOADED);
  const [isVersionMatching, setIsVersionMatching] = useState(true);
  // Guards the appConfig-driven stale-build cleanup so it runs at most once per
  // distinct BUILD_VERSION value (idempotent regardless, via the localStorage marker).
  const staleBuildCleanupRanRef = useRef<string | null>(null);

  const handleRefresh = async () => {
    // Verify with a real probe instead of trusting `navigator.onLine` — otherwise a
    // false-negative `navigator.onLine` made this button silently do nothing.
    const online = await probeConnectivity();
    if (online) {
      setNetworkError(false);
      setIsVersionMatching(true);
      window.location.reload();
    }
    // else: still unreachable → stay on the offline page.
  };

  async function checkIfUserHasDeletedAccountInOtherDevice(userId: string) {
    if (!userId) return;
    const response = await getUserDetailsApi(accessJWT, userId);
    if (!response || response?.length === 0 || JSON.stringify(response) === "{}") {
      handleLogoutClick();
    }
  }

  async function fetchPromptTemplate(accessToken: string) {
    try {
      const response: any = await getPromptTemplateAPI(accessToken);
      consoleLog(["PROMPT TEMPLATE", response]);
      if (response?.data) {
        setPromptTemplate(response.data);
      }
    } catch (error) {
      console.error("Error fetching config:", error);
    }
  }

  async function fetchConfigWithFilterCategory(accessToken: string, category: string) {
    try {
      const response: any = await getConfigWithFilter(accessToken, category, 0, 100);
      if (response?.statusCode !== 200) {
        handleCustomError(response?.statusCode, "config.details");
        // clearStorage(["_zenvoya_JWT"]); // local helper in App.tsx
        window.localStorage.removeItem("_zenvoya_JWT");
        window.sessionStorage.removeItem("_zenvoya_JWT");
        return null;
      }
      if (response?.data) {
        setAppConfig(response?.data?.data);
      }
    } catch (error) {
      console.error("Error fetching config:", error);
    }
  }

  const fetchAppMessages = async () => {
    try {
      const response = await getAppToastMessage(accessJWT);
      const data = response?.data || [];
      updateToastMessages(data);
    } catch (err) {
      console.error("Error fetching app messages", err);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function getUserLocation() {
    const response = await getUserLocationAPI(accessJWT);
    setUserLocation(response ? response : {});
  }

  // From checkConfigLoaded
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function getAccessToken() {
    // This logic was in App.tsx getAccessToken function, which used ACCESS_JWT global constant
    return ""; // Placeholder, needs access to ACCESS_JWT
  }

  useEffect(() => {
    checkIfUserHasDeletedAccountInOtherDevice(userInfo?.id);
  }, []);

  useEffect(() => {
    if (!isValidJWT(accessJWT)) return;
    // Non-critical for first paint — defer to idle time.
    const run = () => { fetchAppMessages(); };
    const w = window as any;
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(run, { timeout: 2000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = setTimeout(run, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (isValidJWT(accessJWT)) {
      // Fire both in parallel — previously fetchPromptTemplate was gated behind a
      // 500ms setTimeout that just added wall-clock delay with no benefit.
      fetchConfigWithFilterCategory(accessJWT, "zenvoyaApp");
      fetchPromptTemplate(accessJWT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessJWT, isValidAccessToken]); // Added deps

  // Stale-build cleanup — SOLE trigger. Once appConfig arrives, run cleanupStaleBuild
  // with the admin-controlled APP_CONFIG.BUILD_VERSION value (there is no boot-time /
  // bundle-baked caller — single source of truth, so nothing can clobber the marker).
  // appConfig needs a valid JWT (guest or logged-in), so this covers every user; auth
  // is preserved by the wipe, so it never logs anyone out. The ref keeps it to one run
  // per distinct value (idempotent regardless, via the localStorage marker).
  useEffect(() => {
    const active = resolveBoolFlag(appConfig, COMMON_APP_CONFIG.ACTIVATE_BUILD_CLEANUP);
    const configBuildVersion = appConfig?.find((ele) => ele?.key === COMMON_APP_CONFIG.BUILD_VERSION)?.value;
    if (!active || !configBuildVersion) return;
    if (staleBuildCleanupRanRef.current === configBuildVersion) return;
    staleBuildCleanupRanRef.current = configBuildVersion;
    cleanupStaleBuild(configBuildVersion, active);
  }, [appConfig]);


  useEffect(() => {
    setJoiValidationEnabled(resolveBoolFlag(appConfig, COMMON_APP_CONFIG.ENABLE_JOI_VALIDATION));
  }, [appConfig]);

  useEffect(() => {
    async function fetchDeviceId() {
      const id = await getDeviceId();
      const data = getItemFromLocalStorage(LOCAL_STORAGE_KEY.deviceId);
      if (!data) {
        setItemInLocalStorage(LOCAL_STORAGE_KEY.deviceId, { id: id, count: 0 });
      }
    }
    fetchDeviceId();

    // Real connectivity detection — EVENT-DRIVEN (no steady background polling while
    // online, so the network tab stays clean). We probe on mount, on the browser
    // online/offline events, and on tab re-focus; once a failure streak begins we
    // fast-poll every FAILING_RECHECK_MS until it either lasts OFFLINE_THRESHOLD_MS
    // (show the page) or a probe succeeds (hide + stop). Recursive setTimeout (not
    // setInterval) + an in-flight guard so probes never overlap.
    let cancelled = false;
    let timerId: number;
    let firstFailureAt: number | null = null; // start of the current continuous-failure streak
    let probing = false;

    const tick = async () => {
      if (cancelled || probing) return;
      probing = true;
      const online = await probeConnectivity();
      probing = false;
      if (cancelled) return;
      if (online) {
        firstFailureAt = null;
        setNetworkError(false);
        return; // online → don't reschedule; wait for the next event
      }
      if (firstFailureAt === null) firstFailureAt = Date.now();
      // Debounce is measured from the START of the failure streak, so a long idle
      // period (no probes) can't skip the OFFLINE_THRESHOLD_MS window.
      if (Date.now() - firstFailureAt >= OFFLINE_THRESHOLD_MS) {
        setNetworkError(true);
        // getConfigMessage needs appConfig.
        setErrorMessage(getConfigMessage(appConfig, COMMON_APP_CONFIG.NO_INTERNET_MESSAGE.info));
      }
      timerId = window.setTimeout(tick, FAILING_RECHECK_MS); // keep checking while failing
    };
    tick(); // establish state on mount

    // Re-probe on the events that can change connectivity. These only TRIGGER a probe
    // (the offline event is itself navigator.onLine-based, i.e. unreliable) — the probe
    // decides. This is what wakes detection while online (no steady poll). onVisibility
    // catches a connection change that happened while the tab was backgrounded.
    const rerun = () => {
      if (cancelled) return;
      clearTimeout(timerId);
      tick();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") rerun();
    };
    window.addEventListener("online", rerun);
    window.addEventListener("offline", rerun);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
      window.removeEventListener("online", rerun);
      window.removeEventListener("offline", rerun);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Added appConfig? If appConfig changes, message might change. But strictly, empty array means run once.

  // Fallback loading timeout
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Refresh the anonymous guest token when it's MISSING *or EXPIRED* (and the user
    // isn't logged in). Previously this only fired on `!accessJWT`, so an expired-but-
    // present passKey JWT in localStorage was never replaced — it survived reloads and
    // kept short-circuiting every pre-login API call (incl. verify-captcha) to null,
    // which is why a plain refresh didn't fix the Turnstile loop but clearing site data
    // (which wipes the stale localStorage token) did. The `!userInfo?.loggedIn` guard is
    // preserved, so a logged-in user's token is never touched here.
    if ((!accessJWT || !isValidJWT(accessJWT)) && !userInfo?.loggedIn) {
      const interval = setInterval(() => {
        const guestToken = ACCESS_JWT || (window as any).ACCESS_JWT || "";
        if (guestToken) {
          setAccessJWT(guestToken);
          setLoading(false);
          clearInterval(interval);
        }
      }, 500);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [userInfo?.loggedIn, accessJWT, setAccessJWT]);

  useGlobalAction((event) => {
    const API_VERSION = appConfig?.find((ele: any) => ele?.key === "APP_VERSION")?.value;
    if (API_VERSION && API_VERSION !== APP_VERSION) setIsVersionMatching(false);
    else setIsVersionMatching(true);
  });

  // Country fetching
  useEffect(() => {
    if (countriesData?.length === 0 && userInfo?.loggedIn) {
      if (!isValidJWT(accessJWT)) clearAllLocalSessionStorageThenRefresh(setAccessJWT);
      else
        fetchCountries(accessJWT).then((response) => {
          setLoading(false);
          if (response.statusCode === 200) {
            setCountriesData(response.data);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countriesData?.length, fetchCountries]); // Simplified

  // Auto-detect the user's country (ISO-2) from their IP via the already-integrated
  // Radar geocode endpoint, cached in localStorage, so the booking traveler-form phone
  // field can pre-select the country code by browser LOCATION (see resolveDefaultCountry).
  // Fire-and-forget, once per browser (guarded on no cached value) once the JWT is valid.
  // Isolated from the shared `userLocation` global (which feeds other payloads).
  useEffect(() => {
    if (!isValidJWT(accessJWT)) return;
    if (getCachedGeoCountryIso()) return;
    fetchAndCacheGeoCountry(accessJWT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessJWT]);

  return { loading, setLoading, networkError, errorMessage, envLoading, isVersionMatching, handleRefresh };
};
